import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking, success, expired, error
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          await refreshUser();
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attempts.current >= 8) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (attempts.current >= 8) {
          setState("error");
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [params, refreshUser]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-8">
      <div className="max-w-md w-full bg-white border border-zinc-200 rounded-sm p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold mb-2">Confirmando pagamento...</h2>
            <p className="text-sm text-zinc-500">Isso pode levar alguns segundos.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="h-16 w-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle2 className="h-9 w-9 text-green-600" strokeWidth={2.5} />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2">Pagamento confirmado!</h2>
            <p className="text-sm text-zinc-600 mb-6">
              Seu plano <strong>{info?.plan_key === "pro" ? "Pro" : "Agência"}</strong> está ativo.
              Aproveite todos os recursos.
            </p>
            <Button
              onClick={() => navigate("/painel")}
              data-testid="payment-success-cta"
              className="rounded-sm bg-blue-600 hover:bg-blue-700 h-11 px-6"
            >
              Ir para o Painel <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </>
        )}
        {state === "expired" && (
          <>
            <XCircle className="h-12 w-12 text-amber-600 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold mb-2">Sessão expirada</h2>
            <p className="text-sm text-zinc-500 mb-6">Por favor, tente novamente.</p>
            <Button onClick={() => navigate("/planos")} className="rounded-sm">
              Ver planos
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold mb-2">Não foi possível confirmar</h2>
            <p className="text-sm text-zinc-500 mb-6">
              Se você completou o pagamento, atualize a página em alguns segundos. O webhook do Stripe ativará seu plano automaticamente.
            </p>
            <Button onClick={() => navigate("/planos")} className="rounded-sm">
              Voltar aos planos
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
